import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePollingRunDetail, isRunInProgress } from '../hooks/useRunExplorer';
import { buildTurnModel, findEntryNodeId, summarizeRun } from '../utils/runTurnModel';
import { isGraphDefinition } from '../utils/definitionTransform';
import { RunHeader } from '../components/runExplorer/RunHeader';
import { TurnStrip } from '../components/runExplorer/TurnStrip';
import { RunPathCanvas } from '../components/runExplorer/RunPathCanvas';
import { RunTimeline, type RunSelection } from '../components/runExplorer/RunTimeline';
import { StepDetailPanel } from '../components/runExplorer/StepDetailPanel';
import { RunJournal } from '../components/runExplorer/RunJournal';
import { RunCollaborationPanel } from '../components/runExplorer/RunCollaborationPanel';

interface RunExplorerPageProps {
  /** Ids from the caller; when absent they are read from the route. */
  collaborationId?: string;
  runId?: string;
  /** Page chrome above the header: the AI Service's back link. */
  backLink?: ReactNode;
  /** Badges beside the status: test and schedule provenance. */
  badges?: ReactNode;
}

function errorMessage(error: unknown): string {
  // The api client rethrows failures as an Error carrying the HTTP `status`.
  const status = (error as { status?: number } | null)?.status;
  if (status === 404) return 'This run was not found.';
  if (status === 401 || status === 403) return 'You do not have access to this run.';
  return 'The run could not be loaded. Please try again.';
}

/**
 * The Run Explorer (task 1151): one page for one collaboration run, built
 * around the path it took. Reached from Advanced › Collaborations, an AI
 * Service's Runs tab (inside the service's chrome, through
 * `EndpointRunDetailPage`), and **View run** on an Assistant reply card, which
 * opens it on that reply's turn with `?turn=N` (0-based).
 *
 * Under the turn strip, the Collaboration panel (task 1306) shows the selected
 * turn as a conversation — what came in and what went out — above the path,
 * timeline and journal, which stay the technical detail.
 *
 * While the run is in progress the page re-reads it every five seconds.
 */
export default function RunExplorerPage({ collaborationId: idProp, runId: runIdProp, backLink, badges }: RunExplorerPageProps) {
  const params = useParams<{ id?: string; collaborationId?: string; runId?: string }>();
  const [searchParams] = useSearchParams();
  const collaborationId = idProp ?? params.collaborationId ?? params.id;
  const runId = runIdProp ?? params.runId ?? collaborationId;

  const { data: detail, isLoading, error } = usePollingRunDetail(collaborationId, runId);

  const definition = useMemo(
    () => (detail && isGraphDefinition(detail.definition) ? detail.definition : null),
    [detail]
  );
  const turns = useMemo(
    () => (detail ? buildTurnModel(detail.journal ?? [], detail.nodeData ?? [], definition, detail.children ?? []) : []),
    [detail, definition]
  );
  const summary = useMemo(() => summarizeRun(turns, definition, detail?.children ?? []), [turns, definition, detail]);

  const requestedTurn = Number.parseInt(searchParams.get('turn') ?? '', 10);
  const [selectedTurnIndex, setSelectedTurnIndex] = useState<number | null>(null);
  const [selection, setSelection] = useState<RunSelection | null>(null);

  // Default turn: the one the link asked for, else the last. Re-evaluated as a
  // polled run gains turns, until the User picks one.
  const lastTurn = Math.max(turns.length - 1, 0);
  const turnIndex =
    selectedTurnIndex ??
    (Number.isInteger(requestedTurn) && requestedTurn >= 0 && requestedTurn <= lastTurn ? requestedTurn : lastTurn);
  const turn = turns.find((t) => t.index === turnIndex) ?? null;

  useEffect(() => {
    // A selection from another turn does not carry over.
    if (selection && turn && !turn.steps[selection.stepId]) setSelection(null);
  }, [turn, selection]);

  if (isLoading) {
    return (
      <div>
        {backLink}
        <div className="flex items-center justify-center h-64" data-testid="run-explorer-loading">
          <div className="text-gray-500 dark:text-gray-400">Loading run…</div>
        </div>
      </div>
    );
  }

  if (error || !detail || !collaborationId || !runId) {
    return (
      <div>
        {backLink}
        <div className="flex items-center justify-center h-64" data-testid="run-explorer-error">
          <div className="text-red-500 dark:text-red-400">{errorMessage(error)}</div>
        </div>
      </div>
    );
  }

  const panelClass = 'bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4';

  return (
    <div className="space-y-4" data-testid="run-explorer">
      {backLink}
      <RunHeader
        detail={detail}
        runId={runId}
        turnCount={turns.length}
        summary={summary}
        span={{ startedAt: turns[0]?.startedAt ?? null, endedAt: turns[turns.length - 1]?.endedAt ?? null }}
        badges={badges}
      />
      {isRunInProgress(detail) && (
        <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="run-refreshing">
          In progress · refreshing every 5 seconds
        </p>
      )}
      <TurnStrip
        turns={turns}
        selectedTurnIndex={turnIndex}
        onSelect={(index) => {
          setSelectedTurnIndex(index);
          setSelection(null);
        }}
      />
      <div className={panelClass}>
        <RunCollaborationPanel
          detail={detail}
          turns={turns}
          turn={turn}
          definition={definition}
          entryStepId={findEntryNodeId(definition, detail.journal ?? [])}
        />
      </div>
      <div className={panelClass}>
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          Path{turns.length > 1 ? ` taken in turn ${turnIndex + 1}` : ''}
        </h2>
        <RunPathCanvas
          definition={definition}
          turn={turn}
          selectedStepId={selection?.stepId ?? null}
          onSelectStep={(stepId) => setSelection({ stepId })}
        />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className={`${panelClass} xl:col-span-3 overflow-x-auto`}>
          <RunTimeline
            turns={turns}
            selectedTurnIndex={turnIndex}
            children={detail.children ?? []}
            selection={selection}
            onSelect={setSelection}
          />
        </div>
        <div className={`${panelClass} xl:col-span-2`}>
          <StepDetailPanel
            collaborationId={collaborationId}
            turn={turn}
            selection={selection}
            definition={definition}
            outputMapping={detail.collaboration.collaborationConfig?.outputMapping ?? null}
            children={detail.children ?? []}
            onSelect={setSelection}
          />
        </div>
      </div>
      <div className={panelClass}>
        <RunJournal collaborationId={collaborationId} turns={turns} selectedTurnIndex={turnIndex} />
      </div>
    </div>
  );
}
